import { useState } from 'react';
import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { myActivities, myTasks, seriesInUse, visiblePulse } from '../state/selectors';
import { Avatar, Btn, Card, CardTitle, Chip, ChipRow, EmptyState, Eyebrow, Mono, Photo, Tag, initials, safeTop } from '../components/ui';
import { Ticker } from '../components/chrome';
import { CommitmentCard } from '../components/CommitmentCard';
import { PulseCard } from '../components/PulseCard';
import { TaskRow } from '../components/TaskRow';
import { TICKER_EXTRA } from '../data/seed';
import { isoWeek, weekdayShort, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { isDemo } from '../lib/clock';
import { getSession } from '../services/auth';
import { homeTheme } from '../theme/homeThemes';

export function Home() {
  const { state: { data, ui }, act, live } = useApp();
  const localProto = live && getSession().authMode === 'local-dev';
  const kitFor = (id?: string) => (id && data.shareKits[id] ? id : isDemo ? 'kit-london' : undefined);
  const nowMs = useNow();
  const [limit, setLimit] = useState(6);
  const me = data.ambassadors[data.me];
  const today = ymdIn(nowMs, me.timeZone);
  const mine = myActivities(data);
  const lead = mine[0];
  const led = mine.filter((a) => a.organiserId === data.me).length;
  const tasks = myTasks(data, today);
  const openCount = tasks.filter((t) => !t.done).length;
  const feed = visiblePulse(data, ui.pulseFilter);
  const moving = Object.values(data.activities).filter((a) => a.stage > 0 && a.stage < 4).length;
  const needHelp = new Set(data.pulse.filter((p) => p.kind === 'help').map((p) => p.activityId)).size;
  const [, mm, dd] = today.split('-');
  // The signed-in member's own appearance (server-stored). Only this page uses it.
  const t = homeTheme(data.appearance?.theme);
  const bgUrl = data.appearance?.backgroundUrl;

  const quick = lead
    ? [
        { glyph: '↻', label: 'Update progress', onClick: () => act.openSheet({ kind: 'update', activityId: lead.id }) },
        { glyph: '✎', label: 'Post update', onClick: () => navigate({ name: 'create', sub: 'post' }) },
        { glyph: '?', label: 'Request help', onClick: () => act.openSheet({ kind: 'help', activityId: lead.id }) },
        { glyph: '⇄', label: 'Coordinate', onClick: () => navigate({ name: 'collab', id: lead.id }) },
        ...(kitFor(lead.shareKitId) ? [{ glyph: '↗', label: 'Share', onClick: () => navigate({ name: 'share', id: kitFor(lead.shareKitId)! }) }] : []),
      ]
    : [];

  return (
    <div className="fade" style={{ position: 'relative', minHeight: '100%', background: t.bg, color: t.fg }}>
      {bgUrl && (
        <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 360, backgroundImage: `url("${bgUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(${t.rgb}, .74) 0%, rgba(${t.rgb}, .84) 62%, rgb(${t.rgb}) 100%)` }} />
        </div>
      )}
      <div style={{ position: 'relative' }}>
      <header style={{ padding: `${safeTop(16)} 20px 0`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={`${import.meta.env.BASE_URL}brand/unit-mark-violet.svg`} alt="UNIT" style={{ height: 22, width: 'auto' }} />
          <Mono size={10} color={t.muted} style={{ letterSpacing: '.06em' }}>{`WK ${isoWeek(today)} · ${weekdayShort(today)} ${dd}.${mm}`}</Mono>
          {!live && <Tag label="DEMO" bg={C.amberBg} fg={C.amber} />}
          {localProto && <Tag label="LOCAL PROTOTYPE" bg={C.amberBg} fg={C.amber} />}
        </div>
        <button type="button" onClick={() => navigate({ name: 'me' })} aria-label="Open your profile" style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', padding: 0, minHeight: 44 }}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ font: font(600, 13), color: t.fg }}>{me.name}</span>
            <Mono size={9} color={t.accent} style={{ letterSpacing: '.04em' }}>{(me.city ? `${me.city} · ${me.title.replace(me.city, '').trim()}` : me.title).toUpperCase()}</Mono>
          </span>
          {me.photoUrl ? <Avatar person={me} size={36} /> : <span style={{ width: 36, height: 36, borderRadius: '50%', background: me.avatar.bg, color: me.avatar.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', font: font(700, 13, 1) }}>{initials(me.name)}</span>}
        </button>
      </header>

      <h1 aria-label="UGAIN" style={{ margin: 0, padding: '6px 16px 0', fontFamily: 'Geist, system-ui, sans-serif', fontWeight: 800, fontSize: 'min(88px, 26vw)', lineHeight: 0.84, letterSpacing: '-.07em' }}>UGAIN</h1>

      <Ticker items={['U GAIN. WE ALL GAIN.', `${moving} ACTIVITIES MOVING THIS WEEK`, `${needHelp} TEAMS NEED HELP`, ...(isDemo ? [TICKER_EXTRA] : [])]} />

      <div style={{ padding: '22px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Eyebrow spacing={0.3} color={t.fg}>{led ? "You're organising" : 'Your commitments'}</Eyebrow>
        <Mono color={t.muted}>{`${led} LEAD · ${mine.length - led} SUPPORTING`}</Mono>
      </div>
      {mine.length ? (
        <div className="noscroll" aria-label="Your commitments" style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory', padding: '24px 12px 4px', scrollPaddingLeft: 12 }}>
          {mine.map((a) => <CommitmentCard key={a.id} activity={a} single={mine.length === 1} />)}
        </div>
      ) : (
        <EmptyState title="No commitments yet." body="Find an activity to support in Discover, or propose your own." action={<Btn variant="primary" onClick={() => navigate({ name: 'create', sub: 'propose' })}>Propose an activity</Btn>} />
      )}

      <Card style={{ padding: '18px 18px 6px' }}>
        <CardTitle right={<Mono size={11} weight={600} color={C.violet}>{openCount ? `${openCount} open` : 'All done'}</Mono>}>My tasks</CardTitle>
        <div style={{ marginTop: 4 }}>
          {tasks.map((t) => {
            const a = data.activities[t.activityId];
            return <TaskRow key={t.id} task={t} today={today} context={`${a.name.replace(/^London /, '')} · ${a.team.find((m) => m.ambassadorId === data.me)?.role ?? ''}`} onOpen={() => navigate({ name: 'activity', id: a.id })} />;
          })}
          {!tasks.length && <p style={{ font: font(400, 13), color: C.grey, padding: '8px 0 12px', margin: 0 }}>Nothing assigned to you right now.</p>}
        </div>
      </Card>

      {quick.length > 0 && (
        <div className="noscroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '18px 12px 0' }}>
          {quick.map((q) => (
            <button key={q.label} type="button" onClick={q.onClick} className="press"
              style={{ flex: 'none', height: 50, padding: '0 18px', borderRadius: R.pill, border: t.dark ? '1px solid rgba(255,255,255,.22)' : 0, background: C.ink, color: C.white, font: font(700, 14), display: 'flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true" style={{ color: C.rim, fontSize: '1rem' }}>{q.glyph}</span>{q.label}
            </button>
          ))}
        </div>
      )}

      <section aria-labelledby="pulse-title" style={{ margin: '22px 0 0', background: C.night, color: C.white, padding: '26px 0' }}>
        <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <h2 id="pulse-title" style={{ margin: 0, font: font(700, 34, 0.9), letterSpacing: '-.045em' }}>UGAIN Pulse</h2>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: font(600, 10), letterSpacing: '.2em' }}>
            <span className="live-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: C.mark }} />{isDemo ? 'DEMO FEED' : !live ? 'NOT LIVE' : localProto ? 'LOCAL PROTOTYPE' : 'LIVE'}
          </span>
        </div>
        <p style={{ margin: 0, padding: '6px 20px 0', font: font(400, 13, 1.4), color: C.mutedDark }}>What ambassadors are getting done, worldwide.</p>
        <ChipRow padding="14px 20px 0">
          {['All', 'Mine', 'Help wanted', ...seriesInUse(data).map((s) => s.id)].map((f) => <Chip key={f} dark label={f} selected={ui.pulseFilter === f} onClick={() => { act.setPulseFilter(f); setLimit(6); }} />)}
        </ChipRow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 12px 0' }}>
          {feed.slice(0, limit).map((item) => <PulseCard key={item.id} item={item} />)}
          {!feed.length && <p style={{ margin: 0, padding: '20px 8px', font: font(400, 14, 1.45), color: C.mutedDark }}>Nothing here yet for this filter.</p>}
          {feed.length > limit && <Btn variant="ghostDark" full onClick={() => setLimit((l) => l + 6)}>Show more</Btn>}
        </div>
      </section>

      {isDemo && <Card style={{ margin: '16px 12px 0', padding: 16, display: 'grid', gridTemplateColumns: '72px 1fr', gap: 14, alignItems: 'center' }}>
        <div style={{ width: 72, height: 72 }}><Photo id="coin-tokyo" pad={0} /></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Eyebrow color={C.violet}>Announcement</Eyebrow>
          <span style={{ font: font(600, 15, 1.25) }}>City UNITS weekly release. #1 Tokyo UNIT is out in BETA.</span>
          <button type="button" onClick={() => navigate({ name: 'share', id: 'kit-tokyo' })} style={{ alignSelf: 'flex-start', border: 0, background: 'transparent', padding: '8px 0', color: C.violet, font: font(600, 13) }}>Open share kit ›</button>
        </div>
      </Card>}
      </div>
    </div>
  );
}
